import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

import styles from './Dropdown.module.scss'

import checkIcon from '@/assets/icons/check.svg?url'
import dropdownIcon from '@/assets/icons/dropdown.svg?url'

export type DropdownItem = {
  id: string
  selected: boolean
  title: string
  helperText?: string
  icon?: React.ReactNode
  sectionIndex?: number
  disabled?: boolean
}

interface DropdownProps {
  items: DropdownItem[]
  /** Omit for a controlled dropdown that derives `items` from a prop rather than local state. */
  setItems?: React.Dispatch<React.SetStateAction<DropdownItem[]>>
  onSelect?: (selectedItems: DropdownItem[]) => void
  placeholder?: string
  dropdownChildren?: React.ReactNode
  searchable?: boolean
  multiDropdown?: boolean
  className?: string
  disabled?: boolean
  ariaLabel?: string
  /** Overrides the default 150px box width — needed when placeholder/selection text wraps to 2 lines. */
  width?: number | string
  /** Closed-state label. Skips the selected-title / chip trigger. */
  summary?: string
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  setItems,
  onSelect,
  placeholder = 'Dropdown',
  dropdownChildren,
  searchable = false,
  multiDropdown = false,
  className,
  disabled = false,
  ariaLabel,
  width,
  summary,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchFor, setSearchFor] = useState('')
  const [focusedItem, setFocusedItem] = useState(-1)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const optionId = (index: number) => `${listboxId}-option-${index}`

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedItems = useMemo(() => items.filter((item) => item.selected), [items])

  const filteredItems = useMemo(() => {
    const searchTerm = searchFor.toLowerCase()
    const startsWithItems = items.filter((item) => item.title.toLowerCase().startsWith(searchTerm))
    const includesItems = items.filter(
      (item) => item.title.toLowerCase().includes(searchTerm) && !item.title.toLowerCase().startsWith(searchTerm)
    )
    return [...startsWithItems, ...includesItems]
  }, [items, searchFor])

  const groupedItems = useMemo(() => {
    const grouped = filteredItems.reduce(
      (acc, item) => {
        const sectionIndex = item.sectionIndex ?? 0
        if (!acc[sectionIndex]) {
          acc[sectionIndex] = []
        }
        acc[sectionIndex].push(item)
        return acc
      },
      {} as Record<number, DropdownItem[]>
    )

    return Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b))
  }, [filteredItems])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFor(e.target.value)
    setFocusedItem(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        if (filteredItems.length === 0) break
        e.preventDefault()
        setIsOpen(true)
        setFocusedItem((prev) => (prev < 0 ? 0 : (prev + 1) % filteredItems.length))
        break
      case 'ArrowUp':
        if (filteredItems.length === 0) break
        e.preventDefault()
        setIsOpen(true)
        setFocusedItem((prev) =>
          prev < 0 ? filteredItems.length - 1 : (prev - 1 + filteredItems.length) % filteredItems.length
        )
        break
      case 'Enter':
      case ' ':
        if (e.key === ' ' && searchable) break
        if (!isOpen) {
          e.preventDefault()
          setIsOpen(true)
          setFocusedItem(
            Math.max(
              0,
              filteredItems.findIndex((item) => item.selected)
            )
          )
          break
        }
        if (focusedItem >= 0 && focusedItem < filteredItems.length) {
          e.preventDefault()
          handleDropdownItem(filteredItems[focusedItem])
          setSearchFor('')
          if (!multiDropdown) setIsOpen(false)
        }
        break
      case 'Tab':
        if (focusedItem >= 0 && focusedItem < filteredItems.length) {
          e.preventDefault()
          handleDropdownItem(filteredItems[focusedItem])
          setSearchFor('')
          if (!multiDropdown) setIsOpen(false)
        }
        break
      case 'Escape':
        if (!isOpen) break
        e.preventDefault()
        // DialogWrapper listens on document; without this a dropdown inside a dialog closes both.
        e.stopPropagation()
        setIsOpen(false)
        break
      default:
        break
    }
  }

  const handleDropdownItem = (item: DropdownItem) => {
    if (item.disabled) return
    const nextItems = items.map((prevItem) =>
      prevItem.id === item.id
        ? { ...prevItem, selected: multiDropdown ? !prevItem.selected : true }
        : { ...prevItem, selected: multiDropdown ? prevItem.selected : false }
    )
    setItems?.((prev) =>
      prev.map((prevItem) =>
        prevItem.id === item.id
          ? {
              ...prevItem,
              selected: multiDropdown ? !prevItem.selected : true,
            }
          : {
              ...prevItem,
              selected: multiDropdown ? prevItem.selected : false,
            }
      )
    )
    if (!multiDropdown) {
      setIsOpen(false)
      onSelect?.([item])
    } else {
      onSelect?.(nextItems)
    }
  }

  return (
    <div
      ref={dropdownRef}
      className={`${styles.selectContainer} ${className || ''} ${disabled ? styles.disabled : ''}`}
      style={width !== undefined ? { width } : undefined}
    >
      <div
        className={`${styles.selectMain} ${
          !multiDropdown ? styles.singleDropdown : ''
        } ${!searchable ? styles.notSearchable : styles.searchable}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={!searchable && !disabled ? handleKeyDown : undefined}
        role={!searchable ? 'button' : undefined}
        tabIndex={!searchable ? 0 : undefined}
        aria-label={!searchable ? ariaLabel : undefined}
        aria-haspopup={!searchable ? 'listbox' : undefined}
        aria-expanded={!searchable ? isOpen : undefined}
        aria-disabled={!searchable ? disabled : undefined}
        aria-controls={!searchable && isOpen ? listboxId : undefined}
        aria-activedescendant={
          !searchable && isOpen && focusedItem >= 0 && focusedItem < filteredItems.length
            ? optionId(focusedItem)
            : undefined
        }
      >
        <div className={styles.selectMainContent}>
          {summary && (
            <span className={styles.selectMainText} aria-live="polite" aria-atomic="true">
              {summary}
            </span>
          )}
          {!summary && !multiDropdown && !isInputFocused && (
            <span className={styles.selectMainText}>
              {selectedItems.length > 0 ? selectedItems.map((item) => item.title).join(', ') : placeholder}
            </span>
          )}
          {!summary && multiDropdown && (
            <>
              {selectedItems.length === 0 && <span className={styles.selectPlaceholder}>{placeholder}</span>}
              {selectedItems.map((item) => (
                <div key={item.id} className={styles.selectedItem}>
                  {item.title}
                  <button
                    className={styles.removeItemButton}
                    onClick={() => handleDropdownItem(item)}
                    aria-label={`Remove ${item.title}`}
                  >
                    <img src={dropdownIcon} alt="" />
                    {/* <IoCloseOutline /> */}
                  </button>
                </div>
              ))}
            </>
          )}
          {searchable && (
            <input
              className={`${styles.searchInput} ${isInputFocused ? styles.inputFocused : ''}`}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onChange={handleInputChange}
              value={searchFor}
              placeholder={
                selectedItems.length === 0
                  ? 'Dropdown...'
                  : !multiDropdown && isInputFocused
                    ? selectedItems[0]?.title
                    : ''
              }
              ref={inputRef}
              onKeyDown={handleKeyDown}
            />
          )}
        </div>
        <div className={`${styles.iconContainer} ${multiDropdown ? styles.multiDropdownIcon : ''}`}>
          <img src={dropdownIcon} alt="" />
          {/* {isInputFocused ? (
            <IoSearch className={styles.icon} />
          ) : (
            <span
              className={`${styles.chevronContainer} ${
                isOpen ? styles.rotateUp : ""
              }`}
            >
              <IoChevronDown className={styles.icon} />
            </span>
          )} */}
        </div>
      </div>
      {isOpen && (
        <div className={styles.dropdown} role="listbox" id={listboxId}>
          {filteredItems.length === 0 && (
            <div className={styles.noMatches}>
              <span>No matches found</span>
            </div>
          )}
          {groupedItems.map(([, items], groupIndex) => (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && <div className={styles.sectionDivider} />}
              {items.map((item) => (
                <div
                  key={item.id}
                  id={optionId(filteredItems.indexOf(item))}
                  className={`${styles.dropdownItem} ${item.selected ? styles.selected : ''} ${
                    item.disabled ? styles.itemDisabled : ''
                  } ${focusedItem === filteredItems.indexOf(item) ? styles.focused : ''}`}
                  onMouseDown={() => handleDropdownItem(item)}
                  role="option"
                  aria-label={item.title}
                  aria-selected={item.selected}
                  aria-disabled={item.disabled || undefined}
                  aria-describedby={item.helperText ? `${optionId(filteredItems.indexOf(item))}-help` : undefined}
                >
                  <img
                    src={checkIcon}
                    className={`${styles.checkIcon} ${item.selected ? styles.visible : ''}`}
                    alt=""
                  />
                  <div className={styles.dropdownItemContent}>
                    <div className={styles.dropdownItemTitle}>{item.title}</div>
                    {item.helperText && (
                      <div id={`${optionId(filteredItems.indexOf(item))}-help`} className={styles.dropdownItemHelper}>
                        {item.helperText}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
          {dropdownChildren}
        </div>
      )}
    </div>
  )
}

export { Dropdown }
